import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Snackbar,
  Avatar,
  Stack,
  Grid,
  Divider,
  Paper,
  Chip,
  IconButton,
  InputAdornment,
  useTheme, // Added for theme access
} from '@mui/material';
import {
  Refresh,
  CheckCircle,
  Cancel,
  Visibility,
  VisibilityOff,
  Lock,
  Person,
  ShoppingBag,
  LocationOn,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { getPendingOrders, approveOrder } from '../../services/orderService';
import { getCurrencyRates } from '../../services/currency';
import CustomPagination from '../../components/common/CustomPagination';

/**
 * Currency conversion utility
 */
const currency_converter = (defaultCurrency, preferredCurrency, price, rate) => {
  if (defaultCurrency === preferredCurrency) {
    return price;
  }
  if (defaultCurrency === 'NGN') {
    return price / rate;
  }
  return price * rate;
};



/**
 * Round number to 2 decimal places
 */
const round_to_two = (number) => {
  if (number == null || isNaN(number) || !isFinite(number)) {
    return '0.00';
  }
  const result = +(Math.round(Number(number) * 100) / 100).toFixed(2);
  return result.toLocaleString(undefined, {maximumFractionDigits: 2, minimumFractionDigits: 2});
};

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

const PendingApproval = () => {
  const { t } = useTranslation(['orders', 'common']);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const theme = useTheme(); // Initialize useTheme
  const [currencyRate, setCurrencyRate] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });


  // Pagination state
const [page, setPage] = useState(0);
const [rowsPerPage, setRowsPerPage] = useState(10);
const [customRowsInput, setCustomRowsInput] = useState('');
const [customRowsActive, setCustomRowsActive] = useState(false);
const [totalCount, setTotalCount] = useState(0);
  
  // Approval dialog state
  const [approvalDialog, setApprovalDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [approvalPin, setApprovalPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [approving, setApproving] = useState(false);
  
  // Format price helper
  const formatPrice = (price, rate) => {
    if (!rate || !price) {
      return `¥${parseFloat(price || 0).toFixed(2)}`;
    }
    const convertedPrice = currency_converter('CNY', 'NGN', parseFloat(price), rate);
    return `₦${round_to_two(convertedPrice)}`;
  };
  
  // Fetch currency rate
  useEffect(() => {
    const fetchCurrencyRate = async () => {
      try {
        const response = await getCurrencyRates({ page: 1, page_size: 100 });
        const rates = response.results || [];
        const ngnToCnyRate = rates.find(rate => 
          rate.base_currency_code === 'NGN' && rate.target_currency_code === 'CNY'
        );
        if (ngnToCnyRate && ngnToCnyRate.base_currency_rate) {
          setCurrencyRate(parseFloat(ngnToCnyRate.base_currency_rate));
        }
      } catch (error) {
        console.error('Failed to fetch currency rate:', error);
      }
    };
    fetchCurrencyRate();
  }, []);
  
  // Load pending orders
  const loadPendingOrders = async () => {
    try {
      setLoading(true);
      const response = await getPendingOrders({
        page: page + 1,
        page_size: rowsPerPage,
      });
      
      if (response.status === 'success') {
        setOrders(response.results || []);
        setTotalCount(response.count || 0);
      } else {
        showSnackbar(response.message || 'Failed to load pending orders', 'error');
      }
    } catch (error) {
      console.error('Error loading pending orders:', error);
      showSnackbar('Failed to load pending orders: ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadPendingOrders();
  }, [page, rowsPerPage]);
  
  // Handle approval
  const handleApproveClick = (order) => {
    setSelectedOrder(order);
    setApprovalPin('');
    setApprovalDialog(true);
  };
  
  const handleApprove = async () => {
    if (!approvalPin || approvalPin.length !== 6 || !/^\d{6}$/.test(approvalPin)) {
      showSnackbar('Please enter a valid 6-digit PIN', 'error');
      return;
    }
    
    try {
      setApproving(true);
      const response = await approveOrder(selectedOrder.id, approvalPin);
      
      if (response.status === 'success') {
        showSnackbar('Order approved successfully. Payment deducted from customer wallet.', 'success');
        setApprovalDialog(false);
        setSelectedOrder(null);
        setApprovalPin('');
        // Reload orders
        loadPendingOrders();
      } else {
        showSnackbar(response.message || 'Failed to approve order', 'error');
      }
    } catch (error) {
      console.error('Error approving order:', error);
      showSnackbar(
        error.response?.data?.message || 'Failed to approve order: ' + error.message,
        'error'
      );
    } finally {
      setApproving(false);
    }
  };
  
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };


    // Pagination handlers
    const handlePageChange = (newPage) => {
      setPage(newPage);
    };
  
    const handleCustomRowsInputChange = (e) => {
      setCustomRowsInput(e.target.value.replace(/\D/g, ''));
    };
  
    const handleCustomRowsInputBlur = () => {
      const val = parseInt(customRowsInput, 10);
      if (val && val > 0) {
        setRowsPerPage(val);
        setPage(0);
      }
      setCustomRowsActive(false);
    };
  
    const handleRowsPerPageChange = (e) => {
      const value = e.target.value;
      if (value === 'custom') {
        setCustomRowsActive(true);
        setCustomRowsInput(rowsPerPage.toString());
      } else {
        setRowsPerPage(Number(value));
        setPage(0);
        setCustomRowsActive(false);
      }
    };
  
  // DataGrid columns
  const columns = [
    {
      field: 'sn',
      headerName: '#',
      width: 80,
      sortable: false,
      filterable: false,
    },
    {
      field: 'order_number',
      headerName: 'Order Number',
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'user',
      headerName: 'Customer',
      width: 200,
      renderCell: (params) => {
        const user = params.value;
        if (typeof user === 'object' && user) {
          return (
            <Box>
              <Typography variant="body2">{user.username || 'N/A'}</Typography>
              <Typography variant="caption" color="text.secondary">
                {user.phone || ''}
              </Typography>
            </Box>
          );
        }
        return 'N/A';
      },
    },
    {
      field: 'title',
      headerName: 'Product',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'total_price',
      headerName: 'Total (RMB)',
      width: 120,
      renderCell: (params) => `¥${parseFloat(params.value || 0).toFixed(2)}`,
    },
    {
      field: 'total_price_ngn',
      headerName: 'Total (NGN)',
      width: 140,
      renderCell: (params) => {
        const totalPrice = params.row.total_price;
        return formatPrice(totalPrice, currencyRate);
      },
    },
    {
      field: 'placed_by',
      headerName: 'Created By',
      width: 150,
      renderCell: (params) => {
        const admin = params.value;
        if (typeof admin === 'object' && admin) {
          return admin.username || 'N/A';
        }
        return 'N/A';
      },
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      width: 180,
      renderCell: (params) => {
        if (!params.value) return 'N/A';
        const date = new Date(params.value);
        return date.toLocaleString();
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Button
          variant="contained"
          size="small"
          startIcon={<CheckCircle />}
          onClick={() => handleApproveClick(params.row)}
          sx={{
            backgroundColor: '#4CAF50',
            '&:hover': { backgroundColor: '#45a049' },
          }}
        >
          Approve
        </Button>
      ),
    },
  ];
  
  // Add serial numbers
  const addSerialNumbers = (data, page, rowsPerPage) =>
    data.map((row, idx) => ({
      ...row,
      sn: (page * rowsPerPage) + idx + 1,
    }));
  
  const totalPages = Math.ceil(totalCount / rowsPerPage) || 1;
  
  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
          Pending Orders Approval
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadPendingOrders}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>
      
      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        These are orders created by admins on behalf of customers. They require approval before payment is deducted.
        Only admins with approval permission can approve orders. Order creators cannot approve their own orders (except superusers).
      </Alert>
      
      {/* Orders Table */}
      <Card>
        <CardContent>
          {loading && orders.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : orders.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Typography variant="h6" color="text.secondary">
                No pending orders
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                All orders have been approved or there are no orders awaiting approval.
              </Typography>
            </Box>
          ) : (
            <DataGrid
              rows={addSerialNumbers(orders, page, rowsPerPage)}
              columns={columns}
              getRowId={(row) => row.id}
              pageSize={rowsPerPage}
              rowsPerPageOptions={[]}
              disableRowSelectionOnClick
              loading={loading}
              hideFooter
              disableColumnMenu
      disableColumnFilter
      disableColumnSelector
      autoHeight
      sx={{
        border: 'none',
        '& .MuiDataGrid-columnHeader': {
          backgroundColor: theme.palette.primary.main, // Use theme primary color
        },
        '& .MuiDataGrid-columnHeaderTitle': {
          color: theme.palette.primary.contrastText, // Use theme contrast text color
          fontWeight: 'bold',
        },
        '& .MuiDataGrid-row:hover': {
          backgroundColor: theme.palette.action.hover,
        },
        // Custom header for serial number
        '& .MuiDataGrid-columnHeader[data-field="sn"]': {
          backgroundColor: theme.palette.primary.main,
        },
        '& .MuiDataGrid-columnHeader[data-field="sn"] .MuiDataGrid-columnHeaderTitle': {
          color: theme.palette.primary.contrastText,
        },
      }}
            />
          )}
        </CardContent>
      </Card>
      
      {/* Pagination */}
      {/* Custom Pagination */}
      <CustomPagination
        page={page}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        customRowsActive={customRowsActive}
        customRowsInput={customRowsInput}
        onCustomRowsInputChange={handleCustomRowsInputChange}
        onCustomRowsInputBlur={handleCustomRowsInputBlur}
      />
      
      {/* Approval Dialog */}
      <Dialog
        open={approvalDialog}
        onClose={() => !approving && setApprovalDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Lock color="primary" />
            <Typography variant="h6">Approve Order</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="warning">
                You are about to approve this order. Payment will be deducted from the customer's wallet.
              </Alert>
              
              <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Order Details
                </Typography>
                <Typography variant="body2">
                  <strong>Order Number:</strong> {selectedOrder.order_number}
                </Typography>
                <Typography variant="body2">
                  <strong>Customer:</strong> {selectedOrder.user?.username || 'N/A'} ({selectedOrder.user?.phone || 'N/A'})
                </Typography>
                <Typography variant="body2">
                  <strong>Product:</strong> {selectedOrder.title}
                </Typography>
                <Typography variant="body2">
                  <strong>Total (RMB):</strong> ¥{parseFloat(selectedOrder.total_price || 0).toFixed(2)}
                </Typography>
                <Typography variant="body2">
                  <strong>Total (NGN):</strong> {formatPrice(selectedOrder.total_price, currencyRate)}
                </Typography>
                <Typography variant="body2">
                  <strong>Created By:</strong> {selectedOrder.placed_by?.username || 'N/A'}
                </Typography>
              </Paper>
              
              <TextField
                fullWidth
                label="Approval PIN"
                type={showPin ? 'text' : 'password'}
                value={approvalPin}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setApprovalPin(value);
                }}
                placeholder="Enter 6-digit PIN"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPin(!showPin)}
                        edge="end"
                      >
                        {showPin ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                helperText={approvalPin.length > 0 && approvalPin.length < 6 ? 'PIN must be 6 digits' : ''}
                error={approvalPin.length > 0 && approvalPin.length < 6}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setApprovalDialog(false);
              setApprovalPin('');
              setSelectedOrder(null);
            }}
            disabled={approving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleApprove}
            disabled={approving || approvalPin.length !== 6}
            startIcon={approving ? <CircularProgress size={20} /> : <CheckCircle />}
            sx={{
              backgroundColor: '#4CAF50',
              '&:hover': { backgroundColor: '#45a049' },
            }}
          >
            {approving ? 'Approving...' : 'Approve Order'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PendingApproval;

